import { describeDomains, noArgsSchema } from "../operation-meta.js";
import { commentsByEntityQueryArgsSchema, idQueryArgsSchema, myTasksQueryArgsSchema, projectGroupIDQueryArgsSchema, projectIDQueryArgsSchema, } from "../projects-input-schemas.js";
import { ForbiddenError, NotFoundError } from "../../errors.js";
import { assertAdminOnlyFlagWriteAccess, assertAdminOnlyRowAccess, assertMeetingNoteWriteAccess, canSeeAdminOnlyContent, assertTaskWriteAccess, replaceMeetingNoteMentions, replaceMeetingNoteTagAssignments, toZeroDate, } from "../projects-helpers.js";
import { assertCanEditProject, canReadProject, isStaff, scopeToReadableProjects, } from "../project-access.js";
/**
 * Freelancers may edit an entity linked to several (or no) projects only when
 * there is at least one link and every linked project is editable.
 */
export function assertCanEditAllProjects(ctx, projectIDs, message) {
    if (isStaff(ctx))
        return;
    const ids = [...new Set(projectIDs.filter((id) => !!id))];
    if (ctx.role !== "freelancer" || ids.length === 0) {
        throw new ForbiddenError(message);
    }
    for (const id of ids)
        assertCanEditProject(ctx, id);
}
function authorizedTasksQuery(ctx, projectZql) {
    if (isStaff(ctx)) {
        return projectZql.tasks;
    }
    // Freelancers also see tasks linked to projects they collaborate on.
    if (ctx.role === "freelancer" && ctx.clientSide)
        return projectZql.tasks;
    const projectIDs = ctx.role === "freelancer" ? [...(ctx.readableProjectIDs ?? [])] : [];
    const personID = ctx.personID;
    if (!personID && projectIDs.length === 0) {
        return projectZql.tasks.where("id", "__unauthorized__");
    }
    return projectZql.tasks.where(({ exists, or }) => or(...(personID
        ? [
            exists("assignees", (assignee) => assignee.where("personID", personID)),
        ]
        : []), ...(projectIDs.length > 0
        ? [
            exists("projects", (taskProject) => taskProject.where("projectID", "IN", projectIDs)),
        ]
        : [])));
}
/** Staff edit every task, freelancers tasks of their editable projects. */
async function assertTaskRowWriteAccess(tx, ctx, projectZql, taskID) {
    if (isStaff(ctx))
        return;
    if (ctx.role !== "freelancer" ||
        (!ctx.clientSide && !ctx.editableProjectIDs?.length)) {
        throw new ForbiddenError("Not allowed to edit tasks");
    }
    const links = await tx.run(projectZql.taskProjects.where("taskID", taskID));
    assertCanEditAllProjects(ctx, (links ?? []).map((link) => link.projectID), "Not allowed to edit tasks");
}
function tasksWithRelations(ctx, projectZql) {
    // A task can be linked to projects the caller can't see; only attach the
    // readable ones (and no project groups) for non-staff.
    return authorizedTasksQuery(ctx, projectZql)
        .related("assignees", (assignee) => assignee.related("person"))
        .related("projects", (taskProject) => scopeToReadableProjects(ctx, taskProject).related("project"))
        .related("projectGroups", (taskProjectGroup) => (isStaff(ctx)
        ? taskProjectGroup
        : taskProjectGroup.where("id", "__unauthorized__")).related("projectGroup"));
}
function authorizedNotificationsQuery(ctx, projectZql) {
    return ctx.userID
        ? projectZql.notifications.where("userID", ctx.userID)
        : projectZql.notifications.where("id", "__unauthorized__");
}
function authorizedToolFavoritesQuery(ctx, projectZql) {
    return ctx.userID
        ? projectZql.toolFavorites.where("userID", ctx.userID)
        : projectZql.toolFavorites.where("toolID", "__unauthorized__");
}
const taskStateLabels = {
    backlog: "Backlog",
    todo: "To do",
    in_progress: "In progress",
    done: "Done",
};
function zeroDateLabel(value) {
    return value == null
        ? "No due date"
        : new Date(value).toISOString().slice(0, 10);
}
export function describeTaskUpdate(task, args) {
    const changes = [];
    if (args.title !== undefined && args.title !== task.title) {
        changes.push(`Title: “${task.title}” → “${args.title}”`);
    }
    if (args.description !== undefined &&
        args.description !== (task.description ?? null)) {
        const before = task.description?.trim();
        const after = args.description?.trim();
        changes.push(!before
            ? "Description added"
            : !after
                ? "Description removed"
                : "Description changed");
    }
    if (args.state !== undefined && args.state !== task.state) {
        changes.push(`State: ${taskStateLabels[task.state]} → ${taskStateLabels[args.state]}`);
    }
    if (args.dueDate !== undefined) {
        const nextDueDate = toZeroDate(args.dueDate);
        if (nextDueDate !== (task.dueDate ?? null)) {
            changes.push(`Due date: ${zeroDateLabel(task.dueDate)} → ${zeroDateLabel(nextDueDate)}`);
        }
    }
    return changes.join("; ");
}
async function notifyTaskUpdate({ tx, ctx, projectZql, task, args, }) {
    if (!ctx.userID)
        return;
    const body = describeTaskUpdate(task, args);
    if (!body)
        return;
    const updater = await tx.run(projectZql.people.where("userID", ctx.userID).one());
    const updaterName = updater
        ? `${updater.firstName} ${updater.lastName}`.trim()
        : "Someone";
    const updatedTitle = args.title ?? task.title;
    const recipientUserIDs = new Set((task.assignees ?? [])
        .map((assignee) => assignee.person?.userID)
        .filter((userID) => !!userID && userID !== ctx.userID));
    for (const userID of recipientUserIDs) {
        await tx.mutate.notifications.insert({
            id: crypto.randomUUID(),
            userID,
            type: "task_updated",
            title: `${updaterName} updated “${updatedTitle}”`,
            body,
            entityType: "task",
            entityID: task.id,
            sourceTaskID: task.id,
            actorID: ctx.userID,
            isRead: false,
        });
    }
}
async function notifyTaskAssignee({ tx, ctx, projectZql, taskID, personID, }) {
    const assignee = await tx.run(projectZql.people.where("id", personID).one());
    const assigneeUserID = assignee?.userID;
    if (!assigneeUserID || assigneeUserID === ctx.userID)
        return;
    const task = await tx.run(projectZql.tasks.where("id", taskID).one());
    if (!task)
        return;
    const assigner = ctx.userID
        ? await tx.run(projectZql.people.where("userID", ctx.userID).one())
        : null;
    const assignerName = assigner
        ? `${assigner.firstName} ${assigner.lastName}`.trim()
        : "";
    const title = `${assignerName || "Someone"} assigned you a task`;
    const existingNotification = await tx.run(projectZql.notifications
        .where("userID", assigneeUserID)
        .where("sourceTaskID", taskID)
        .where("type", "task_assigned")
        .one());
    if (existingNotification) {
        await tx.mutate.notifications.update({
            id: existingNotification.id,
            title,
            body: task.title,
            entityType: "task",
            entityID: task.id,
            sourceTaskID: task.id,
            actorID: ctx.userID ?? null,
            isRead: false,
        });
        return;
    }
    await tx.mutate.notifications.insert({
        id: crypto.randomUUID(),
        userID: assigneeUserID,
        type: "task_assigned",
        title,
        body: task.title,
        entityType: "task",
        entityID: task.id,
        sourceTaskID: task.id,
        actorID: ctx.userID ?? null,
        isRead: false,
    });
}
function authorizedCommentsQuery(ctx, projectZql) {
    if (canSeeAdminOnlyContent(ctx)) {
        return projectZql.comments;
    }
    // Comments on managers-only meeting notes are as restricted as the note.
    if (ctx.role === "employee") {
        return projectZql.comments.where(({ cmp, exists, or }) => or(cmp("entityType", "!=", "meeting_note"), exists("meetingNote", (note) => note.where("adminOnly", false))));
    }
    if (ctx.role === "freelancer" && ctx.clientSide) {
        return projectZql.comments.where(({ cmp, exists, or }) => or(cmp("entityType", "!=", "meeting_note"), exists("meetingNote", (note) => collaboratorVisibleMeetingNotes(note))));
    }
    // Freelancers see comments on their projects and on their projects' notes.
    const projectIDs = ctx.role === "freelancer" ? [...(ctx.readableProjectIDs ?? [])] : [];
    if (projectIDs.length === 0) {
        return projectZql.comments.where("id", "__unauthorized__");
    }
    return projectZql.comments.where(({ and, cmp, exists, or }) => or(and(cmp("entityType", "project"), cmp("entityID", "IN", projectIDs)), and(cmp("entityType", "meeting_note"), exists("meetingNote", (note) => collaboratorVisibleMeetingNotes(note).where("projectID", "IN", projectIDs)))));
}
function commentsWithRelations(ctx, projectZql) {
    return authorizedCommentsQuery(ctx, projectZql)
        .related("author")
        .related("resolver")
        .related("mentions", (mention) => mention.related("people"));
}
/**
 * Meeting notes freelancer collaborators may see: neither managers-only nor
 * scoped to employees (`hiddenFromCollaborators`).
 */
function collaboratorVisibleMeetingNotes(notes) {
    return notes
        .where("adminOnly", false)
        .where("hiddenFromCollaborators", false);
}
/** Rejects non-staff access to notes scoped to employees. */
function assertMeetingNoteCollaboratorAccess(ctx, note) {
    if (note?.hiddenFromCollaborators && !isStaff(ctx)) {
        throw new ForbiddenError("Not allowed to access this meeting note");
    }
}
/** Only staff choose whether collaborators see a meeting note. */
function assertCollaboratorScopeWriteAccess(ctx) {
    if (!isStaff(ctx)) {
        throw new ForbiddenError("Only employees can change a meeting note's visibility");
    }
}
/**
 * Rejects comment writes on managers-only meeting notes for non-admins.
 * Freelancers only comment on editable projects and their meeting notes,
 * never on notes scoped to employees.
 */
async function assertCommentEntityAccess(tx, ctx, projectZql, entity) {
    const note = entity?.entityType === "meeting_note" && entity.entityID
        ? await tx.run(projectZql.meetingNotes.where("id", entity.entityID).one())
        : null;
    assertAdminOnlyRowAccess(ctx, note);
    if (isStaff(ctx))
        return;
    assertMeetingNoteCollaboratorAccess(ctx, note);
    assertCanEditAllProjects(ctx, [entity?.entityType === "project" ? entity.entityID : note?.projectID], "Not allowed to edit comments");
}
function authorizedMeetingNotesQuery(ctx, projectZql) {
    if (canSeeAdminOnlyContent(ctx)) {
        return projectZql.meetingNotes;
    }
    if (ctx.role === "employee") {
        return projectZql.meetingNotes.where("adminOnly", false);
    }
    // Freelancers see the notes of projects they collaborate on, except notes
    // scoped to employees.
    return scopeToReadableProjects(ctx, collaboratorVisibleMeetingNotes(projectZql.meetingNotes));
}
/** Staff edit every note, freelancers notes of their editable projects. */
function assertMeetingNoteRowWriteAccess(ctx, note) {
    if (isStaff(ctx)) {
        assertMeetingNoteWriteAccess(ctx);
    }
    else {
        assertMeetingNoteCollaboratorAccess(ctx, note);
        assertCanEditAllProjects(ctx, [note?.projectID], "Not allowed to edit meeting notes");
    }
    assertAdminOnlyRowAccess(ctx, note);
}
function meetingNotesWithRelations(ctx, projectZql) {
    return authorizedMeetingNotesQuery(ctx, projectZql)
        .related("document")
        .related("tagAssignments", (assignment) => assignment.related("tag"))
        .related("mentions", (mention) => mention.related("people"));
}
function meetingNotesWithContext(ctx, projectZql) {
    return meetingNotesWithRelations(ctx, projectZql)
        .related("project")
        .related("projectGroup");
}
function authorizedMeetingNoteTagsQuery(ctx, projectZql) {
    // Freelancers need the tag list to tag notes of their projects.
    if (isStaff(ctx) || ctx.role === "freelancer") {
        return projectZql.meetingNoteTags;
    }
    return projectZql.meetingNoteTags.where("id", "__unauthorized__");
}
export function createCollaborationQueries({ defineProjectQuery, projectZql, }) {
    return {
        tasks: {
            list: defineProjectQuery({
                input: noArgsSchema,
                description: "All tasks visible to the caller, newest first, with assignees (and their person), linked projects and linked project groups. Admins and employees see every task, other users only tasks assigned to them; freelancers also tasks linked to projects they collaborate on. Dates (dueDate, createdAt, ...) are epoch ms.",
            }, ({ ctx }) => tasksWithRelations(ctx, projectZql).orderBy("createdAt", "desc")),
            mine: defineProjectQuery({
                input: myTasksQueryArgsSchema,
                description: "The open (not done) tasks assigned to the caller, newest first, with assignees, linked projects and project groups. Returns nothing unless personID is the caller's own person ID. Dates are epoch ms.",
            }, ({ args, ctx, }) => {
                if (!ctx.personID || ctx.personID !== args.personID) {
                    return projectZql.tasks.where("id", "__unauthorized__");
                }
                return tasksWithRelations(ctx, projectZql)
                    .whereExists("assignees", (assignee) => assignee.where("personID", args.personID))
                    .where("state", "!=", "done")
                    .orderBy("createdAt", "desc");
            }),
            byID: defineProjectQuery({
                input: idQueryArgsSchema,
                description: "One task by ID, or null, with assignees, linked projects and project groups. Same visibility as tasks.list. Dates are epoch ms.",
            }, ({ args, ctx }) => tasksWithRelations(ctx, projectZql).where("id", args.id).one()),
        },
        notifications: {
            listMine: defineProjectQuery({
                input: noArgsSchema,
                description: "The caller's 50 most recent notifications, newest first. createdAt is epoch ms.",
            }, ({ ctx }) => authorizedNotificationsQuery(ctx, projectZql)
                .orderBy("createdAt", "desc")
                .limit(50)),
        },
        toolFavorites: {
            listMine: defineProjectQuery({
                input: noArgsSchema,
                description: "The tools the caller has marked as favorites.",
            }, ({ ctx }) => authorizedToolFavoritesQuery(ctx, projectZql)),
        },
        comments: {
            byEntity: defineProjectQuery({
                input: commentsByEntityQueryArgsSchema,
                description: "Comments on one entity, oldest first, with author, resolver and mentioned people. Admins and employees; freelancers only comments on projects they collaborate on and on those projects' meeting notes. Comments on managers-only meeting notes are visible to admins only, comments on notes hidden from collaborators to admins and employees only. createdAt is epoch ms.",
            }, ({ args, ctx, }) => commentsWithRelations(ctx, projectZql)
                .where("entityType", args.entityType)
                .where("entityID", args.entityID)
                .orderBy("createdAt", "asc")),
        },
        meetingNotes: {
            commandItems: defineProjectQuery(noArgsSchema, ({ ctx }) => authorizedMeetingNotesQuery(ctx, projectZql).orderBy("meetingDate", "desc")),
            all: defineProjectQuery({
                input: noArgsSchema,
                description: "All meeting notes visible to the caller, latest meeting first, with document, tags, mentioned people, project and project group. Admins see every note, employees only notes not marked managers-only (adminOnly), freelancers only notes of projects they collaborate on that are neither managers-only nor hidden from collaborators (hiddenFromCollaborators), others none. meetingDate is epoch ms; read a note's body via GET /api/v1/documents/{documentID}/markdown.",
            }, ({ ctx }) => meetingNotesWithContext(ctx, projectZql).orderBy("meetingDate", "desc")),
            byID: defineProjectQuery({
                input: idQueryArgsSchema,
                description: "One meeting note by ID, or null, with document, tags, mentioned people, project and project group. Same visibility as meetingNotes.all. meetingDate is epoch ms; read the body via GET /api/v1/documents/{documentID}/markdown.",
            }, ({ args, ctx }) => meetingNotesWithContext(ctx, projectZql).where("id", args.id).one()),
            byProject: defineProjectQuery({
                input: projectIDQueryArgsSchema,
                description: "Meeting notes attached to a project, latest meeting first, with document, tags and mentioned people. Same visibility as meetingNotes.all. meetingDate is epoch ms; read a note's body via GET /api/v1/documents/{documentID}/markdown.",
            }, ({ args, ctx }) => (canReadProject(ctx, args.projectID)
                ? meetingNotesWithRelations(ctx, projectZql).where("projectID", args.projectID)
                : projectZql.meetingNotes.where("id", "__unauthorized__")).orderBy("meetingDate", "desc")),
            byProjectGroup: defineProjectQuery(projectGroupIDQueryArgsSchema, ({ args, ctx, }) => meetingNotesWithRelations(ctx, projectZql)
                .where("projectGroupID", args.projectGroupID)
                .orderBy("meetingDate", "desc")),
            byMentionedProject: defineProjectQuery(projectIDQueryArgsSchema, ({ args, ctx }) => meetingNotesWithContext(ctx, projectZql)
                .whereExists("projectMentions", (mention) => mention.where("projectID", args.projectID))
                .orderBy("meetingDate", "desc")),
        },
        meetingNoteTags: {
            list: defineProjectQuery(noArgsSchema, ({ ctx }) => authorizedMeetingNoteTagsQuery(ctx, projectZql).orderBy("name", "asc")),
        },
    };
}
export function createCollaborationMutators({ defineProjectMutator, projectZql, schemas, }) {
    const { createTaskInputSchema, updateTaskInputSchema, taskIDInputSchema, taskAssigneeInputSchema, taskProjectInputSchema, taskProjectGroupInputSchema, markNotificationReadInputSchema, markAllNotificationsReadInputSchema, toolFavoriteInputSchema, createCommentInputSchema, commentIDInputSchema, createMeetingNoteInputSchema, updateMeetingNoteInputSchema, meetingNoteIDInputSchema, createMeetingNoteTagInputSchema, updateMeetingNoteTagInputSchema, meetingNoteTagIDInputSchema, } = schemas;
    return {
        tasks: {
            create: defineProjectMutator({
                input: createTaskInputSchema,
                description: "Create a task. Pass a client-generated `id`; `dueDate` is `YYYY-MM-DD`; `state` is backlog, todo, in_progress or done. Link people and projects afterwards with `taskAssignees.add` and `taskProjects.add`.",
            }, async ({ tx, ctx, args, }) => {
                assertTaskWriteAccess(ctx);
                await tx.mutate.tasks.insert({
                    id: args.id,
                    title: args.title,
                    description: args.description ?? null,
                    state: args.state ?? "backlog",
                    dueDate: toZeroDate(args.dueDate),
                });
            }),
            update: defineProjectMutator({
                input: updateTaskInputSchema,
                description: "Update a task's title, description, state or due date (`YYYY-MM-DD`). At least one field besides `id` is required.",
            }, async ({ tx, ctx, args, }) => {
                await assertTaskRowWriteAccess(tx, ctx, projectZql, args.id);
                const task = await tx.run(projectZql.tasks
                    .where("id", args.id)
                    .related("assignees", (assignee) => assignee.related("person"))
                    .one());
                if (!task)
                    return;
                await tx.mutate.tasks.update({
                    id: args.id,
                    ...(args.title !== undefined ? { title: args.title } : {}),
                    ...(args.description !== undefined
                        ? { description: args.description ?? null }
                        : {}),
                    ...(args.state !== undefined ? { state: args.state } : {}),
                    ...(args.dueDate !== undefined
                        ? { dueDate: toZeroDate(args.dueDate) }
                        : {}),
                });
                await notifyTaskUpdate({ tx, ctx, projectZql, task, args });
            }),
            delete: defineProjectMutator({
                input: taskIDInputSchema,
                description: "Delete a task.",
            }, async ({ tx, ctx, args, }) => {
                await assertTaskRowWriteAccess(tx, ctx, projectZql, args.id);
                await tx.mutate.tasks.delete({ id: args.id });
            }),
        },
        taskAssignees: {
            add: defineProjectMutator({
                input: taskAssigneeInputSchema,
                description: "Assign a person to a task. Pass a client-generated `id` for the link row plus `taskID` and `personID`.",
            }, async ({ tx, ctx, args, }) => {
                await assertTaskRowWriteAccess(tx, ctx, projectZql, args.taskID);
                const existing = await tx.run(projectZql.taskAssignees
                    .where("taskID", args.taskID)
                    .where("personID", args.personID)
                    .one());
                if (existing)
                    return;
                await tx.mutate.taskAssignees.insert(args);
                await notifyTaskAssignee({
                    tx,
                    ctx,
                    projectZql,
                    taskID: args.taskID,
                    personID: args.personID,
                });
            }),
            remove: defineProjectMutator({
                input: taskAssigneeInputSchema,
                description: "Remove a person from a task.",
            }, async ({ tx, ctx, args, }) => {
                await assertTaskRowWriteAccess(tx, ctx, projectZql, args.taskID);
                const existing = await tx.run(projectZql.taskAssignees
                    .where("taskID", args.taskID)
                    .where("personID", args.personID)
                    .one());
                if (existing)
                    await tx.mutate.taskAssignees.delete({ id: existing.id });
            }),
        },
        taskProjects: {
            add: defineProjectMutator({
                input: taskProjectInputSchema,
                description: "Link a task to a project. Pass a client-generated `id` for the link row plus `taskID` and `projectID`.",
            }, async ({ tx, ctx, args, }) => {
                await assertTaskRowWriteAccess(tx, ctx, projectZql, args.taskID);
                assertCanEditAllProjects(ctx, [args.projectID], "Not allowed to edit tasks");
                const existing = await tx.run(projectZql.taskProjects
                    .where("taskID", args.taskID)
                    .where("projectID", args.projectID)
                    .one());
                if (existing)
                    return;
                await tx.mutate.taskProjects.insert(args);
            }),
            remove: defineProjectMutator(taskProjectInputSchema, async ({ tx, ctx, args, }) => {
                if (!isStaff(ctx)) {
                    // Freelancers must not unlink a task from its last project.
                    const links = await tx.run(projectZql.taskProjects.where("taskID", args.taskID));
                    const projectIDs = (links ?? []).map((link) => link.projectID);
                    assertCanEditAllProjects(ctx, projectIDs, "Not allowed to edit tasks");
                    if (projectIDs.every((id) => id === args.projectID)) {
                        throw new ForbiddenError("Not allowed to edit tasks");
                    }
                }
                const existing = await tx.run(projectZql.taskProjects
                    .where("taskID", args.taskID)
                    .where("projectID", args.projectID)
                    .one());
                if (existing)
                    await tx.mutate.taskProjects.delete({ id: existing.id });
            }),
        },
        taskProjectGroups: {
            add: defineProjectMutator(taskProjectGroupInputSchema, async ({ tx, ctx, args, }) => {
                assertTaskWriteAccess(ctx);
                const existing = await tx.run(projectZql.taskProjectGroups
                    .where("taskID", args.taskID)
                    .where("projectGroupID", args.projectGroupID)
                    .one());
                if (existing)
                    return;
                await tx.mutate.taskProjectGroups.insert(args);
            }),
            remove: defineProjectMutator(taskProjectGroupInputSchema, async ({ tx, ctx, args, }) => {
                assertTaskWriteAccess(ctx);
                const existing = await tx.run(projectZql.taskProjectGroups
                    .where("taskID", args.taskID)
                    .where("projectGroupID", args.projectGroupID)
                    .one());
                if (existing) {
                    await tx.mutate.taskProjectGroups.delete({ id: existing.id });
                }
            }),
        },
        notifications: {
            markRead: defineProjectMutator(markNotificationReadInputSchema, async ({ tx, ctx, args, }) => {
                const notification = await tx.run(authorizedNotificationsQuery(ctx, projectZql)
                    .where("id", args.id)
                    .one());
                if (!notification) {
                    throw new NotFoundError("Notification not found");
                }
                await tx.mutate.notifications.update({
                    id: args.id,
                    isRead: true,
                });
            }),
            markAllRead: defineProjectMutator(markAllNotificationsReadInputSchema, async ({ tx, ctx }) => {
                const unread = await tx.run(authorizedNotificationsQuery(ctx, projectZql).where("isRead", false));
                for (const notification of unread ?? []) {
                    await tx.mutate.notifications.update({
                        id: notification.id,
                        isRead: true,
                    });
                }
            }),
        },
        toolFavorites: {
            add: defineProjectMutator({
                input: toolFavoriteInputSchema,
                description: "Mark a tool as a favorite of the caller.",
            }, async ({ tx, ctx, args, }) => {
                if (!ctx.userID)
                    throw new ForbiddenError("Not signed in");
                await tx.mutate.toolFavorites.upsert({
                    userID: ctx.userID,
                    toolID: args.toolID,
                });
            }),
            remove: defineProjectMutator({
                input: toolFavoriteInputSchema,
                description: "Remove a tool from the caller's favorites.",
            }, async ({ tx, ctx, args, }) => {
                if (!ctx.userID)
                    throw new ForbiddenError("Not signed in");
                await tx.mutate.toolFavorites.delete({
                    userID: ctx.userID,
                    toolID: args.toolID,
                });
            }),
        },
        comments: {
            create: defineProjectMutator({
                input: createCommentInputSchema,
                description: "Add a comment to a record (`entityType` + `entityID`, e.g. a project or task). Pass a client-generated `id`; `parentID` replies to a comment.",
            }, async ({ tx, ctx, args, }) => {
                if (!ctx.userID) {
                    throw new ForbiddenError("Not allowed to create comments");
                }
                await assertCommentEntityAccess(tx, ctx, projectZql, args);
                const now = Date.now();
                await tx.mutate.comments.insert({
                    id: args.id,
                    entityType: args.entityType,
                    entityID: args.entityID,
                    parentID: args.parentID ?? null,
                    content: args.content,
                    authorID: ctx.userID,
                    isResolved: false,
                    resolvedBy: null,
                    resolvedAt: null,
                    createdAt: now,
                    updatedAt: now,
                });
                for (const personID of args.mentionedPersonIDs ?? []) {
                    await tx.mutate.commentMentions.insert({
                        id: crypto.randomUUID(),
                        commentID: args.id,
                        personID,
                    });
                }
            }),
            delete: defineProjectMutator(commentIDInputSchema, async ({ tx, ctx, args, }) => {
                const comment = await tx.run(authorizedCommentsQuery(ctx, projectZql).where("id", args.id).one());
                if (!comment) {
                    throw new NotFoundError("Comment not found");
                }
                if (comment.authorID !== ctx.userID) {
                    throw new ForbiddenError("Not allowed to delete this comment");
                }
                await tx.mutate.comments.delete({ id: args.id });
            }),
            resolve: defineProjectMutator(commentIDInputSchema, async ({ tx, ctx, args, }) => {
                if (!ctx.userID) {
                    throw new ForbiddenError("Not allowed to resolve comments");
                }
                await assertCommentEntityAccess(tx, ctx, projectZql, await tx.run(projectZql.comments.where("id", args.id).one()));
                await tx.mutate.comments.update({
                    id: args.id,
                    isResolved: true,
                    resolvedBy: ctx.userID,
                    resolvedAt: Date.now(),
                });
            }),
            unresolve: defineProjectMutator(commentIDInputSchema, async ({ tx, ctx, args, }) => {
                await assertCommentEntityAccess(tx, ctx, projectZql, await tx.run(projectZql.comments.where("id", args.id).one()));
                await tx.mutate.comments.update({
                    id: args.id,
                    isResolved: false,
                    resolvedBy: null,
                    resolvedAt: null,
                });
            }),
        },
        meetingNotes: {
            create: defineProjectMutator({
                input: createMeetingNoteInputSchema,
                description: "Create a meeting note. Pass client-generated `id` and `documentID` (UUIDs); the body is a collaborative document: write it afterwards with `PUT /api/v1/documents/{documentID}/markdown`. `meetingDate` is `YYYY-MM-DD`. Visibility: `adminOnly` (admins only, set by admins), else `hiddenFromCollaborators` (admins and employees, set by staff), else everyone incl. project collaborators (default).",
            }, async ({ tx, ctx, args, }) => {
                if (isStaff(ctx)) {
                    assertMeetingNoteWriteAccess(ctx);
                }
                else {
                    // Freelancers only write notes of their editable projects.
                    assertCanEditAllProjects(ctx, [args.projectID], "Not allowed to edit meeting notes");
                    if (args.projectGroupID) {
                        throw new ForbiddenError("Not allowed to edit meeting notes");
                    }
                }
                if (args.adminOnly)
                    assertAdminOnlyFlagWriteAccess(ctx);
                // Notes freelancers create are visible to everyone ("All").
                if (args.hiddenFromCollaborators) {
                    assertCollaboratorScopeWriteAccess(ctx);
                }
                const now = Date.now();
                await tx.mutate.documents.insert({
                    id: args.documentID,
                    createdAt: now,
                    updatedAt: now,
                });
                await tx.mutate.meetingNotes.insert({
                    id: args.id,
                    title: args.title,
                    content: args.content ?? null,
                    meetingDate: toZeroDate(args.meetingDate),
                    projectID: args.projectID ?? null,
                    projectGroupID: args.projectGroupID ?? null,
                    documentID: args.documentID,
                    adminOnly: args.adminOnly ?? false,
                    hiddenFromCollaborators: args.hiddenFromCollaborators ?? false,
                    createdBy: ctx.userID ?? null,
                    createdAt: now,
                    updatedAt: now,
                });
                await replaceMeetingNoteTagAssignments(tx, args.id, args.tagIDs ?? []);
                await replaceMeetingNoteMentions(tx, args.id, args.mentionedPersonIDs ?? []);
            }),
            update: defineProjectMutator({
                input: updateMeetingNoteInputSchema,
                description: "Update a meeting note's title, date, tags, mentions or visibility (`adminOnly`: admins only; `hiddenFromCollaborators`: staff only). The body is edited through the document Markdown endpoints.",
            }, async ({ tx, ctx, args, }) => {
                if (args.adminOnly !== undefined)
                    assertAdminOnlyFlagWriteAccess(ctx);
                if (args.hiddenFromCollaborators !== undefined) {
                    assertCollaboratorScopeWriteAccess(ctx);
                }
                assertMeetingNoteRowWriteAccess(ctx, await tx.run(projectZql.meetingNotes.where("id", args.id).one()));
                const noteUpdates = {
                    id: args.id,
                    updatedAt: Date.now(),
                };
                if (args.title !== undefined)
                    noteUpdates.title = args.title;
                if (args.content !== undefined)
                    noteUpdates.content = args.content;
                if (args.meetingDate !== undefined) {
                    noteUpdates.meetingDate = toZeroDate(args.meetingDate);
                }
                if (args.adminOnly !== undefined) {
                    noteUpdates.adminOnly = args.adminOnly;
                }
                if (args.hiddenFromCollaborators !== undefined) {
                    noteUpdates.hiddenFromCollaborators = args.hiddenFromCollaborators;
                }
                if (Object.keys(noteUpdates).length > 2) {
                    await tx.mutate.meetingNotes.update(noteUpdates);
                }
                if (args.tagIDs !== undefined) {
                    await replaceMeetingNoteTagAssignments(tx, args.id, args.tagIDs);
                }
                if (args.mentionedPersonIDs !== undefined) {
                    await replaceMeetingNoteMentions(tx, args.id, args.mentionedPersonIDs);
                }
            }),
            delete: defineProjectMutator(meetingNoteIDInputSchema, async ({ tx, ctx, args, }) => {
                const note = await tx.run(projectZql.meetingNotes.where("id", args.id).one());
                assertMeetingNoteRowWriteAccess(ctx, note);
                await tx.mutate.meetingNotes.delete({ id: args.id });
                if (note?.documentID) {
                    await tx.mutate.documents.delete({ id: note.documentID });
                }
            }),
        },
        meetingNoteTags: {
            create: defineProjectMutator(createMeetingNoteTagInputSchema, async ({ tx, ctx, args, }) => {
                assertMeetingNoteWriteAccess(ctx);
                const now = Date.now();
                await tx.mutate.meetingNoteTags.insert({
                    id: args.id,
                    name: args.name,
                    color: args.color ?? null,
                    createdAt: now,
                    updatedAt: now,
                });
            }),
            update: defineProjectMutator(updateMeetingNoteTagInputSchema, async ({ tx, ctx, args, }) => {
                assertMeetingNoteWriteAccess(ctx);
                await tx.mutate.meetingNoteTags.update({
                    id: args.id,
                    ...(args.name !== undefined ? { name: args.name } : {}),
                    ...(args.color !== undefined ? { color: args.color ?? null } : {}),
                    updatedAt: Date.now(),
                });
            }),
            delete: defineProjectMutator(meetingNoteTagIDInputSchema, async ({ tx, ctx, args, }) => {
                assertMeetingNoteWriteAccess(ctx);
                await tx.mutate.meetingNoteTags.delete({ id: args.id });
            }),
        },
    };
}
export const collaborationDomainDescriptions = describeDomains({
    tasks: "To-dos with state, due date and priority, linked to projects or project groups",
    taskAssignees: "People assigned to a task",
    taskProjects: "Links between tasks and projects",
    taskProjectGroups: "Links between tasks and project groups",
    notifications: "The current user's in-app notifications",
    toolFavorites: "The tools the current user has marked as favorites",
    comments: "Comment threads on projects, tasks, quotes and other records",
    meetingNotes: "Meeting notes, project notes and debriefs with a collaborative Markdown body",
    meetingNoteTags: "Tags for categorising meeting notes",
});
